<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { PICTURE_CHOICE_ELEMENT_TYPE } from "@/types/pictureChoice";
import { PRODUCT_TYPE } from "@/util/constants";
import { isLocationCfV1Whitelisted } from "@/util/customFieldsPhase1";
import { legacyDatePickerSidebarField, standardFields } from "@/util/methods";
import { UIDraggableElement, UITextXsMedium } from "@gohighlevel/ghl-ui";
import { ref } from "vue";
import { VueDraggableNext } from "vue-draggable-next";

const store = useAppStore();

if (store.product === PRODUCT_TYPE.FORM) {
  delete standardFields.quizQuestions;
}
if (store.product === PRODUCT_TYPE.SURVEY) {
  delete standardFields.submit;
  delete standardFields.quizQuestions;
}
if (store.product === PRODUCT_TYPE.QUIZ) {
  delete standardFields.submit;
  delete standardFields.integration;
  delete standardFields.choiceElements;
  standardFields.other.group.splice(3, 1);
  standardFields.text.group.splice(0, 2);
}
if (!store.isPaymentEnabled) {
  //Removing paymnet from form
  delete standardFields.integration;
}

// Locations outside the rollout allowlist keep the pre-rollout "Date Picker"
// sidebar entry (legacy label/placeholder, no "New" badge).
if (!isLocationCfV1Whitelisted(store.locationId)) {
  const dateFieldIndex = standardFields.other.group.findIndex(
    (group) => group.tag === "date" && group.dataType === "DATE"
  );
  if (dateFieldIndex > -1) {
    standardFields.other.group[dateFieldIndex] = legacyDatePickerSidebarField();
  }
}

if (![PRODUCT_TYPE.FORM, PRODUCT_TYPE.SURVEY].includes(store.product)) {
  const index = standardFields.address.group.findIndex((group) => group.tag === "group_address");
  if (index > -1) {
    standardFields.address.group.splice(index, 1);
  }
  const addressIndex = standardFields.address.group.findIndex((group) => group.tag === "address");
  if (addressIndex > -1) {
    standardFields.address.group[addressIndex].label = "Address";
    standardFields.address.group[addressIndex].hideInLeftSideBar = false;
  }
}

const quickAdd = ref(standardFields);

/**
 * Keeps rollout-gated Quick Add fields hidden outside the shared Custom Fields
 * Phase 1 location allowlist without affecting already-persisted elements.
 */
const isLocationGatedFieldVisible = (group: any): boolean => {
  if (isLocationCfV1Whitelisted(store.locationId)) return true;

  return (
    group?.dataType !== "URL" &&
    group?.builderElementType !== PICTURE_CHOICE_ELEMENT_TYPE
  );
};

const getDisplayLabel = (group: any): string => {
  if (group?.tag === "header") {
    return "Text";
  }
  const raw = typeof group?.label === "string" ? group.label : "";
  return raw.replace(/<[^>]*>/g, "");
};
</script>
<template>
  <div id="standard-container" class="standard-list-container">
    <div v-for="(category, index) in quickAdd" :key="index" class="flex flex-col gap-[12px]">
      <UITextXsMedium class="mt-2 px-2">{{ category.label }}</UITextXsMedium>
      <VueDraggableNext
        :list="category.group"
        :sort="false"
        class="standard-draggable-container gap-1"
        ghost-class="sortable-ghost"
        :group="{ name: 'elements', pull: 'clone', put: false }"
      >
        <template v-for="(group, i) in category.group" :key="i">
          <div
            v-if="isLocationGatedFieldVisible(group)"
            :class="group.hideInLeftSideBar ? 'hidden' : 'p-1'"
          >
            <UIDraggableElement
              :id="getDisplayLabel(group) + i"
              :icon="(group.icon as any)"
              :label="getDisplayLabel(group)"
            />
          </div>
        </template>
      </VueDraggableNext>
    </div>
  </div>
</template>

<style>
.standard-list-container {
  height: 84vh;
  padding-top: 5px;
  padding-bottom: v-bind("store.isNewBuilderLayoutEnabled ? '100px' : '50px'");
  padding-left: 8px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.element-cards .gui__builder-card {
  display: table;
  height: 100%;
}

#standard-container .gui__builder-card--label {
  padding-bottom: 5px;
  text-overflow: clip;
}

#standard-container .gui__builder-card {
  width: 100px;
  height: 100px;
  padding: 12px 0px;
  position: relative;
}
.standard-item {
  background-color: white;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  cursor: all-scroll;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none;
}
.standard-draggable-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: start;
}
.standard-item {
  box-shadow: 1px 1px 0px 0px #dee2e6;
}
.icon-container {
  width: 15px;
  height: 15px;
  margin: auto 0;
  padding: 0;
  align-items: center;
  display: flex;
}

.gui__builder-card--icon img,
.gui__builder-card--icon svg {
  height: 60px !important;
  width: 60px !important;
}
.gui__builder-card--icon svg {
  height: 35px;
  width: 35px;
}
</style>
